/**
 * Личный календарь (ЗД-05, ЗД-11): дела со сроком из «Ждёт меня», контроль выданных поручений, вехи проектов,
 * платежи и поставки по графикам договоров — месяцем и повесткой, с отбором по проекту. Подписка (ЗД-12):
 * закрытая ссылка .ics для календаря телефона, выпускается и отзывается здесь.
 */
import {
  CALENDAR_KINDS,
  type CalendarEvent,
  type CalendarFeedIssued,
  type CalendarFeedState,
  type CalendarKind,
  INBOX_SOURCES,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import { CalendarDays, Check, ChevronLeft, ChevronRight, Copy, Link2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { api } from '../lib/api';
import { dateTime, date as fmtDate, plural, todayLocal } from '../lib/format';
import { projectsQuery } from '../lib/projects';
import { Button } from '../ui/button';
import { Dialog } from '../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../ui/feedback';
import { Field, Select } from '../ui/form';
import { Page, PageHeader, Panel } from '../ui/layout';

const KIND_DOT: Record<CalendarKind, string> = {
  inbox: 'bg-accent',
  control: 'bg-info',
  milestone: 'bg-ink',
  contract: 'bg-success',
};
const MONTHS = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];
const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const pad = (n: number) => String(n).padStart(2, '0');
const isoOf = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;

/** Сетка месяца: недели с понедельника, дни соседних месяцев — пустые клетки */
function monthGrid(year: number, month: number): Array<Array<string | null>> {
  const first = new Date(Date.UTC(year, month, 1));
  const offset = (first.getUTCDay() + 6) % 7;
  const days = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const cells: Array<string | null> = Array.from({ length: offset }, () => null);
  for (let d = 1; d <= days; d++) cells.push(isoOf(year, month, d));
  while (cells.length % 7) cells.push(null);
  const weeks: Array<Array<string | null>> = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

function EventRow({ e, onOpen }: { e: CalendarEvent; onOpen: (e: CalendarEvent) => void }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(e)}
      className={clsx(
        'flex w-full min-h-11 items-start gap-3 rounded-md px-3 py-2 text-left hover:bg-surface-2',
        e.done && 'opacity-60',
      )}
    >
      <span className={clsx('mt-2 size-2 shrink-0 rounded-full', KIND_DOT[e.kind])} aria-hidden />
      <span className="min-w-0 flex-1">
        <span className={clsx('block text-[14px] text-ink', e.done && 'line-through')}>{e.title}</span>
        <span className="block text-[12px] text-ink-2">
          {[e.projectCode, e.source ? INBOX_SOURCES[e.source] : CALENDAR_KINDS[e.kind], e.details]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>
      {e.overdue ? <Badge tone="danger">просрочено</Badge> : null}
    </button>
  );
}

function FeedDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const state = useQuery({
    queryKey: ['calendar', 'feed'],
    queryFn: () => api<CalendarFeedState>('/calendar/feed'),
  });
  const [issued, setIssued] = useState<CalendarFeedIssued | null>(null);
  const [copied, setCopied] = useState(false);
  // Новая ссылка сразу отзывает прежнюю: при действующей подписке — сначала подтверждение
  const [confirmReissue, setConfirmReissue] = useState(false);
  const issue = useMutation({
    mutationFn: () => api<CalendarFeedIssued>('/calendar/feed', { method: 'POST' }),
    onSuccess: (r) => {
      setIssued(r);
      setConfirmReissue(false);
      qc.invalidateQueries({ queryKey: ['calendar', 'feed'] });
    },
  });
  const revoke = useMutation({
    mutationFn: () => api('/calendar/feed', { method: 'DELETE' }),
    onSuccess: () => {
      setIssued(null);
      qc.invalidateQueries({ queryKey: ['calendar', 'feed'] });
      toast('Подписка отозвана');
    },
  });
  const copy = async () => {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast('Скопируйте ссылку вручную', 'info');
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Подписка на календарь"
      description="События и сроки появляются в календаре телефона сами. Ссылка закрытая: кто её знает, видит ваш календарь без сумм."
      width="lg"
      footer={
        <>
          {state.data?.active ? (
            <Button variant="ghost" loading={revoke.isPending} onClick={() => revoke.mutate()}>
              Отозвать ссылку
            </Button>
          ) : null}
          <Button
            variant="primary"
            loading={issue.isPending}
            onClick={() => (state.data?.active && !confirmReissue ? setConfirmReissue(true) : issue.mutate())}
          >
            {confirmReissue
              ? 'Да, выпустить новую'
              : state.data?.active
                ? 'Выпустить новую ссылку'
                : 'Выпустить ссылку'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4 text-[14px] text-ink">
        {state.data ? (
          <p className="text-ink-2">
            {state.data.active
              ? `Подписка действует с ${dateTime(state.data.createdAt)}${
                  state.data.lastFetchedAt
                    ? `, календарь забирал ленту ${dateTime(state.data.lastFetchedAt)}`
                    : ', календарь ленту ещё не забирал'
                }. Новая ссылка отменяет прежнюю.`
              : 'Подписки пока нет.'}
          </p>
        ) : null}
        {confirmReissue ? (
          <p className="rounded-md border border-warning/40 bg-warning-soft p-3 text-[13px] text-ink">
            Прежняя ссылка перестанет работать: календари, подписанные на неё, перестанут обновляться, пока вы
            не вставите новую.
          </p>
        ) : null}
        {issued ? (
          <div className="rounded-md border border-line bg-surface-2 p-3">
            <p className="text-[13px] text-ink-2">Ссылка показывается один раз — скопируйте её сейчас:</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <code className="min-w-0 flex-1 break-all rounded bg-surface px-2 py-2 text-[12px]">
                {issued.url}
              </code>
              <Button
                variant="secondary"
                size="md"
                icon={copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                onClick={copy}
              >
                {copied ? 'Скопировано' : 'Копировать'}
              </Button>
            </div>
          </div>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <p className="font-medium">Google Calendar</p>
            <p className="mt-1 text-[13px] text-ink-2">
              На компьютере: «Другие календари» → «+» → «Из URL» → вставить ссылку. На телефоне календарь
              появится сам.
            </p>
          </div>
          <div>
            <p className="font-medium">iPhone</p>
            <p className="mt-1 text-[13px] text-ink-2">
              Настройки → Календарь → Учётные записи → Добавить → Другое → «Подписной календарь» → вставить
              ссылку.
            </p>
          </div>
          <div>
            <p className="font-medium">Outlook</p>
            <p className="mt-1 text-[13px] text-ink-2">
              Календарь → «Добавить календарь» → «Подписаться из Интернета» → вставить ссылку.
            </p>
          </div>
        </div>
        <p className="text-[13px] text-ink-2">
          Календарь телефона обновляет ленту сам, обычно раз в час (Google — реже). После отзыва ссылки уже
          скачанные события могут оставаться в календаре, пока вы не удалите подписку в нём.
        </p>
        <ErrorBox error={issue.error ?? revoke.error ?? state.error} />
      </div>
    </Dialog>
  );
}

export function CalendarPage() {
  const navigate = useNavigate();
  const today = todayLocal();
  const [cursor, setCursor] = useState(() => ({
    year: Number(today.slice(0, 4)),
    month: Number(today.slice(5, 7)) - 1,
  }));
  const [view, setView] = useState<'month' | 'agenda'>(() =>
    typeof window !== 'undefined' && window.innerWidth < 768 ? 'agenda' : 'month',
  );
  const [projectId, setProjectId] = useState('');
  const [selected, setSelected] = useState<string>(today);
  const [feedOpen, setFeedOpen] = useState(false);
  const projects = useQuery(projectsQuery());

  const weeks = useMemo(() => monthGrid(cursor.year, cursor.month), [cursor]);
  const from = view === 'month' ? isoOf(cursor.year, cursor.month, 1) : today;
  const to =
    view === 'month'
      ? isoOf(cursor.year, cursor.month, new Date(Date.UTC(cursor.year, cursor.month + 1, 0)).getUTCDate())
      : (() => {
          const d = new Date(`${today}T00:00:00Z`);
          d.setUTCDate(d.getUTCDate() + 60);
          return d.toISOString().slice(0, 10);
        })();
  const events = useQuery({
    queryKey: ['calendar', from, to, projectId],
    queryFn: () =>
      api<CalendarEvent[]>('/calendar', { query: { from, to, ...(projectId ? { projectId } : {}) } }),
  });
  const byDay = useMemo(() => {
    const m = new Map<string, CalendarEvent[]>();
    for (const e of events.data ?? []) {
      const list = m.get(e.date) ?? [];
      list.push(e);
      m.set(e.date, list);
    }
    return m;
  }, [events.data]);
  const open = (e: CalendarEvent) => navigate({ to: e.path });
  const shift = (delta: number) => {
    const d = new Date(Date.UTC(cursor.year, cursor.month + delta, 1));
    setCursor({ year: d.getUTCFullYear(), month: d.getUTCMonth() });
    // Выбранный день — в показанном месяце: сегодня, если месяц текущий, иначе первое число
    const first = d.toISOString().slice(0, 10);
    setSelected(today.slice(0, 7) === first.slice(0, 7) ? today : first);
  };
  const selectedList = byDay.get(selected) ?? [];
  const overdue = (events.data ?? []).filter((e) => e.overdue && !e.done).length;

  return (
    <Page>
      <PageHeader
        title="Календарь"
        subtitle="Дела со сроком, контроль поручений, вехи проектов, платежи и поставки по графикам договоров — по дням"
        actions={
          <Button variant="secondary" icon={<Link2 className="size-4" />} onClick={() => setFeedOpen(true)}>
            Подписка для телефона
          </Button>
        }
      />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="inline-flex rounded-md border border-line p-0.5">
          {(
            [
              ['month', 'Месяц'],
              ['agenda', 'Повестка'],
            ] as const
          ).map(([v, label]) => (
            <button
              key={v}
              type="button"
              aria-pressed={view === v}
              onClick={() => setView(v)}
              className={clsx(
                'h-11 rounded-[4px] px-4 text-[14px] font-medium',
                view === v ? 'bg-ink text-on-ink' : 'text-ink-2 hover:text-ink',
              )}
            >
              {label}
            </button>
          ))}
        </div>
        {view === 'month' ? (
          <div className="inline-flex items-center gap-1">
            <Button
              variant="ghost"
              size="md"
              icon={<ChevronLeft className="size-4" />}
              onClick={() => shift(-1)}
            >
              <span className="sr-only">Предыдущий месяц</span>
            </Button>
            <span className="num min-w-[150px] text-center text-[16px] font-semibold text-ink">
              {MONTHS[cursor.month]} {cursor.year}
            </span>
            <Button
              variant="ghost"
              size="md"
              icon={<ChevronRight className="size-4" />}
              onClick={() => shift(1)}
            >
              <span className="sr-only">Следующий месяц</span>
            </Button>
            <Button
              variant="ghost"
              size="md"
              onClick={() => {
                setCursor({ year: Number(today.slice(0, 4)), month: Number(today.slice(5, 7)) - 1 });
                setSelected(today);
              }}
            >
              Сегодня
            </Button>
          </div>
        ) : null}
        <Field label="Проект" htmlFor="cal-project" className="min-w-[220px]">
          <Select
            id="cal-project"
            className="h-11"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          >
            <option value="">Все проекты</option>
            {(projects.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <div className="ml-auto flex flex-wrap items-center gap-3 text-[12px] text-ink-2">
          {(Object.keys(CALENDAR_KINDS) as CalendarKind[]).map((k) => (
            <span key={k} className="inline-flex items-center gap-1.5">
              <span className={clsx('size-2 rounded-full', KIND_DOT[k])} aria-hidden /> {CALENDAR_KINDS[k]}
            </span>
          ))}
        </div>
      </div>

      {events.isError ? <ErrorBox error={events.error} /> : null}
      {overdue ? (
        <p className="mb-3 text-[14px] text-danger">
          Просрочено: {plural(overdue, 'событие', 'события', 'событий')}
        </p>
      ) : null}

      {view === 'month' ? (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <Panel bodyClassName="p-0">
            <div className="grid grid-cols-7 border-b border-line text-center text-[12px] text-ink-2">
              {WEEKDAYS.map((w) => (
                <div key={w} className="py-2">
                  {w}
                </div>
              ))}
            </div>
            {events.isPending ? (
              <div className="p-6">
                <Spinner />
              </div>
            ) : (
              weeks.map((week, wi) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: недели месяца не переставляются
                <div key={wi} className="grid grid-cols-7 border-b border-line last:border-b-0">
                  {week.map((day, di) =>
                    day ? (
                      <button
                        key={day}
                        type="button"
                        onClick={() => setSelected(day)}
                        aria-pressed={selected === day}
                        aria-label={`${fmtDate(day)}: ${plural(byDay.get(day)?.length ?? 0, 'событие', 'события', 'событий')}`}
                        className={clsx(
                          'flex min-h-[64px] flex-col items-start gap-1 border-r border-line p-1.5 text-left last:border-r-0 hover:bg-surface-2 md:min-h-[96px] md:p-2',
                          selected === day && 'bg-accent-soft/40',
                        )}
                      >
                        <span
                          className={clsx(
                            'num inline-flex size-6 items-center justify-center rounded-full text-[13px]',
                            day === today ? 'bg-ink text-on-ink' : 'text-ink',
                          )}
                        >
                          {Number(day.slice(8, 10))}
                        </span>
                        <span className="hidden w-full flex-col gap-0.5 md:flex">
                          {(byDay.get(day) ?? []).slice(0, 3).map((e) => (
                            <span
                              key={e.id}
                              className={clsx(
                                'truncate rounded px-1 text-[11px] leading-4',
                                e.overdue ? 'bg-danger-soft text-danger' : 'bg-surface-2 text-ink-2',
                              )}
                            >
                              {e.title}
                            </span>
                          ))}
                          {(byDay.get(day)?.length ?? 0) > 3 ? (
                            <span className="text-[11px] text-ink-2">
                              ещё {(byDay.get(day)?.length ?? 0) - 3}
                            </span>
                          ) : null}
                        </span>
                        <span className="flex gap-0.5 md:hidden">
                          {(byDay.get(day) ?? []).slice(0, 4).map((e) => (
                            <span key={e.id} className={clsx('size-1.5 rounded-full', KIND_DOT[e.kind])} />
                          ))}
                        </span>
                      </button>
                    ) : (
                      <div
                        // biome-ignore lint/suspicious/noArrayIndexKey: пустые клетки соседних месяцев
                        key={`empty-${wi}-${di}`}
                        className="border-r border-line bg-surface-2/40 last:border-r-0"
                      />
                    ),
                  )}
                </div>
              ))
            )}
          </Panel>
          <Panel
            title={fmtDate(selected)}
            meta={plural(selectedList.length, 'событие', 'события', 'событий')}
          >
            {selectedList.length ? (
              <div className="-mx-3 flex flex-col">
                {selectedList.map((e) => (
                  <EventRow key={e.id} e={e} onOpen={open} />
                ))}
              </div>
            ) : (
              <p className="text-[14px] text-ink-2">В этот день событий нет.</p>
            )}
          </Panel>
        </div>
      ) : (
        <Panel title="Просроченное и ближайшие 60 дней" bodyClassName="p-0">
          {events.isPending ? (
            <div className="p-6">
              <Spinner />
            </div>
          ) : byDay.size ? (
            [...byDay.entries()].map(([day, list]) => (
              <div key={day} className="border-b border-line px-3 py-2 last:border-b-0">
                <div className="flex items-center gap-2 px-3 py-1 text-[13px] font-medium text-ink">
                  <CalendarDays className="size-4 text-ink-2" aria-hidden />
                  {fmtDate(day)}
                  {day === today ? <Badge tone="ink">сегодня</Badge> : null}
                  {day < today ? <Badge tone="danger">просрочено</Badge> : null}
                </div>
                {list.map((e) => (
                  <EventRow key={e.id} e={e} onOpen={open} />
                ))}
              </div>
            ))
          ) : (
            <div className="p-6">
              <EmptyState title="Ближайших событий нет">
                Дела со сроком, вехи и платежи по графикам появятся здесь.
              </EmptyState>
            </div>
          )}
        </Panel>
      )}
      {feedOpen ? <FeedDialog onClose={() => setFeedOpen(false)} /> : null}
    </Page>
  );
}
